import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff, Home, Shield, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";
const DEVICE_TOKEN_KEY = 'blkmrkt_device_token';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user, token } = useAuth();
  const { addToCart } = useCart();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [saveEmail, setSaveEmail] = useState(() => localStorage.getItem('blkmrkt_save_email') === 'true');
  const [loading, setLoading] = useState(false);
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const hasRedirected = useRef(false);
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('blkmrkt_saved_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setSaveEmail(true);
    }
  }, []);

  const redirect = searchParams.get('redirect');

  // Handle post-login redirect
  useEffect(() => {
    const handleRedirect = async () => {
      if (!user || !token || hasRedirected.current) return;
      
      hasRedirected.current = true;
      
      const pendingProduct = sessionStorage.getItem('blkmrkt_checkout_product');
      const pendingQty = sessionStorage.getItem('blkmrkt_checkout_qty');
      
      if (redirect === 'checkout' && pendingProduct) {
        // Clear storage first
        sessionStorage.removeItem('blkmrkt_checkout_product');
        sessionStorage.removeItem('blkmrkt_checkout_qty');
        
        // Add item to cart
        const qty = parseInt(pendingQty) || 1;
        try {
          await axios.post(
            `${API}/cart/add`,
            { product_id: pendingProduct, quantity: qty },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          navigate('/checkout');
        } catch (err) {
          console.error('Failed to add to cart:', err);
          navigate('/catalog');
        }
      } else {
        navigate('/catalog');
      }
    };
    
    handleRedirect();
  }, [user, token, redirect, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      
      // Save email preference
      if (saveEmail) {
        localStorage.setItem('blkmrkt_saved_email', email);
        localStorage.setItem('blkmrkt_save_email', 'true');
      } else {
        localStorage.removeItem('blkmrkt_saved_email');
        localStorage.setItem('blkmrkt_save_email', 'false');
      }
      
      // First, attempt login to get user data
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const loginData = response.data;
      
      // Check if user is admin and has 2FA enabled
      if (loginData.user?.is_admin) {
        // Check if device is trusted
        const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
        const trustCheck = await axios.post(`${API}/auth/check-trusted-device`, {
          user_id: loginData.user.id,
          device_token: deviceToken
        });
        
        if (!trustCheck.data.trusted && !trustCheck.data['2fa_not_required']) {
          // Need 2FA verification
          setPendingUserId(loginData.user.id);
          setPendingLoginData(loginData);
          setShow2FA(true);
          setLoading(false);
          return;
        }
      }
      
      // Complete login (no 2FA needed or device trusted)
      await completeLogin(loginData);
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Login failed';
      
      // Handle unverified account with a more helpful message
      if (errorDetail === 'Account not verified') {
        toast.error('Your account is not yet verified. Please check your email inbox and spam folder for the verification link.', {
          duration: 8000
        });
      } else {
        toast.error(errorDetail);
      }
      setLoading(false);
    }
  };

  const completeLogin = async (loginData) => {
    // Store token and complete auth
    localStorage.setItem('blkmrkt_token', loginData.token);
    if (rememberMe) {
      localStorage.setItem('blkmrkt_remember', 'true');
    }
    
    // Call the login context to set user state
    await login(email, password, rememberMe);
    
    const firstName = loginData.user.full_name?.split(' ')[0] || 'back';
    toast.success(`WELCOME ${firstName.toUpperCase()}`);
  };

  const handle2FAVerify = async () => {
    if (!twoFACode || twoFACode.length < 6) {
      toast.error('Enter a valid code');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        user_id: pendingUserId,
        code: twoFACode.toUpperCase(),
        remember_device: rememberDevice
      });
      
      // Store device token if returned
      if (response.data.device_token) {
        localStorage.setItem(DEVICE_TOKEN_KEY, response.data.device_token);
      }
      
      // Show warning if backup code was used
      if (response.data.used_backup_code) {
        toast.warning(`Backup code used. ${response.data.backup_codes_remaining} remaining.`);
      }
      
      // Complete the login
      await completeLogin(pendingLoginData);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setLoading(false);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    try {
      setResending(true);
      await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      toast.success('If this email exists, a verification link has been sent.');
      setShowResendForm(false);
      setResendEmail('');
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          <nav className="flex items-center gap-4">
            <Link 
              to="/"
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="home-link"
            >
              <Home className="w-5 h-5" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Login Form */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-sans text-4xl text-white mb-2" data-testid="login-title">
              LOGIN
            </h1>
            <p className="font-mono text-sm text-zinc-500">
              ENTER YOUR CREDENTIALS TO CONTINUE
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-mono text-sm text-zinc-500 mb-2">
                EMAIL
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block font-mono text-sm text-zinc-500 mb-2">
                PASSWORD
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none pr-12"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={setRememberMe}
                className="border-zinc-600 data-[state=checked]:bg-tiffany-500 data-[state=checked]:border-tiffany-500 rounded-none"
                data-testid="remember-me-checkbox"
              />
              <label
                htmlFor="remember"
                className="font-mono text-sm text-zinc-400 cursor-pointer"
              >
                STAY LOGGED IN
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="saveEmail"
                checked={saveEmail}
                onCheckedChange={setSaveEmail}
                className="border-zinc-600 data-[state=checked]:bg-tiffany-500 data-[state=checked]:border-tiffany-500 rounded-none"
                data-testid="save-email-checkbox"
              />
              <label
                htmlFor="saveEmail"
                className="font-mono text-sm text-zinc-400 cursor-pointer"
              >
                REMEMBER MY EMAIL
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans text-lg tracking-widest rounded-none disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? 'AUTHENTICATING...' : 'LOGIN'}
            </Button>
          </form>

          <div className="mt-8 text-center space-y-3">
            <p className="font-mono text-sm text-zinc-500">
              DON'T HAVE AN ACCOUNT?{' '}
              <Link 
                to="/register" 
                className="text-tiffany-500 hover:underline"
                data-testid="register-link"
              >
                REGISTER
              </Link>
            </p>
            <Link 
              to="/forgot-password" 
              className="block font-mono text-sm text-zinc-500 hover:text-tiffany-500 transition-colors"
              data-testid="forgot-password-link"
            >
              FORGOT PASSWORD?
            </Link>
          </div>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowResendForm(!showResendForm)}
              className="font-mono text-sm text-zinc-500 hover:text-tiffany-500 transition-colors"
            >
              RESEND EMAIL VERIFICATION?
            </button>
          </div>

          {showResendForm && (
            <div className="mt-4 p-4 border border-zinc-800 bg-zinc-900/50">
              <p className="font-mono text-xs text-zinc-400 mb-3">
                Enter your email to receive a new verification link
              </p>
              <form onSubmit={handleResendVerification} className="space-y-3">
                <Input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full h-10 bg-zinc-900 border-zinc-700 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  data-testid="resend-email"
                />
                <Button
                  type="submit"
                  disabled={resending}
                  className="w-full h-10 bg-zinc-800 text-white hover:bg-zinc-700 font-mono text-sm rounded-none"
                  data-testid="resend-submit"
                >
                  {resending ? 'SENDING...' : 'RESEND VERIFICATION EMAIL'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>

      {/* 2FA Verification Modal */}
      {show2FA && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-tiffany-500 w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center border border-tiffany-500 bg-tiffany-500/10">
                <Shield className="w-6 h-6 text-tiffany-500" />
              </div>
              <div>
                <h2 className="font-mono text-lg text-white">TWO-FACTOR AUTH</h2>
                <p className="font-mono text-xs text-zinc-500">Enter your authenticator code</p>
              </div>
            </div>
            
            <Input
              type="text"
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="000000"
              className="w-full h-14 text-center text-2xl font-mono tracking-[0.5em] bg-zinc-800 border-zinc-700 text-white mb-4"
              maxLength={8}
              autoFocus
              data-testid="2fa-code-input"
            />
            
            <p className="font-mono text-[10px] text-zinc-600 text-center mb-4">
              Enter the 6-digit code from Google Authenticator or a backup code
            </p>
            
            <div className="flex items-center space-x-2 mb-6">
              <Checkbox
                id="rememberDevice"
                checked={rememberDevice}
                onCheckedChange={setRememberDevice}
                className="border-zinc-600 data-[state=checked]:bg-tiffany-500 data-[state=checked]:border-tiffany-500 rounded-none"
                data-testid="remember-device-checkbox"
              />
              <label
                htmlFor="rememberDevice"
                className="font-mono text-sm text-zinc-400 cursor-pointer"
              >
                REMEMBER THIS DEVICE (30 DAYS)
              </label>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handle2FAVerify}
                disabled={loading || twoFACode.length < 6}
                className="flex-1 h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono disabled:opacity-50"
                data-testid="2fa-verify-button"
              >
                {loading ? 'VERIFYING...' : 'VERIFY'}
              </Button>
              <Button
                onClick={() => {
                  setShow2FA(false);
                  setTwoFACode('');
                  setPendingUserId(null);
                  setPendingLoginData(null);
                }}
                variant="outline"
                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 font-mono"
              >
                CANCEL
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
